
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import { useTranslation } from 'react-i18next';
import { blogPosts } from '../../data/blog-posts';

export default function Blog() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("Alle");

  useEffect(() => {
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "REPUTRUST Blog",
      "description": t('seo.description.blog'),
      "url": "https://reputrust.ai/blog",
      "publisher": {
        "@type": "Organization",
        "name": "SMC Simon Müller Consulting e.K.",
        "logo": {
          "@type": "ImageObject",
          "url": "https://reputrust.ai/logo.png"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, [t]);

  // Dynamically derive categories from blog posts
  const dynamicCategories = ["Alle", ...Array.from(new Set(blogPosts.map(post => post.category)))];

  const filteredPosts = activeCategory === "Alle"
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A]">
      <SEO titleKey="seo.title.blog" descriptionKey="seo.description.blog" />
      <Header />

      <div className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-tron-primary/10 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-tron-secondary/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Hero Section */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full mb-6 shadow-sm">
            <i className="ri-article-line text-white"></i>
            <span className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Orbitron, sans-serif' }}>REPUTRUST INTELLIGENCE</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#0F172A] mb-6 leading-tight break-words hyphens-auto" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            Bewertungsmanagement <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] block sm:inline">Expertenwissen</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Entdecken Sie Tipps, Best Practices und Strategien zur Optimierung Ihrer Online-Reputation und zur Steigerung Ihrer Kundenbewertungen.
          </p>
        </div>

        {/* Categories Filter */}
        <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-md border-y border-slate-200 py-4 mb-16">
          <div className="max-w-[1280px] mx-auto px-6">
            <div className="flex justify-center gap-3 flex-wrap">
              {dynamicCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${activeCategory === category
                    ? "bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white shadow-md border-tron-primary"
                    : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300 hover:text-[#0F172A]"
                    }`}
                  style={{ fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.05em' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="max-w-[1280px] mx-auto px-6 relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[400px]">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-2xl overflow-hidden group cursor-pointer hover:border-blue-300 border border-slate-200 transition-all duration-500 hover:shadow-lg shadow-sm"
                  onClick={() => window.REACT_APP_NAVIGATE(`/blog/${post.slug}`)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-md border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs font-medium shadow-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300 line-clamp-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {post.excerpt}
                    </p>
                    <button
                      className="flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] font-medium text-sm group-hover:gap-3 transition-all uppercase tracking-wider group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A]"
                      style={{ fontFamily: 'Orbitron, sans-serif' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.REACT_APP_NAVIGATE(`/blog/${post.slug}`);
                      }}
                    >
                      Article Read
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-slate-200 border-dashed">
                <i className="ri-article-line text-4xl text-slate-300 mb-4 block"></i>
                <p className="text-slate-400 font-medium font-orbitron">No Data Found</p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          <div className="text-center mt-16">
            <button className="px-8 py-3 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 hover:border-slate-300 hover:text-[#0F172A] transition-all duration-300 font-medium text-sm uppercase tracking-widest backdrop-blur-sm shadow-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              Load More
            </button>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="max-w-[1280px] mx-auto px-6 mt-20">
          <div className="bg-white rounded-2xl p-8 md:p-12 relative overflow-hidden text-center shadow-lg border border-slate-200">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full blur-[80px] opacity-50"></div>

            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-6 border border-blue-100 shadow-sm animate-pulse">
                <i className="ri-mail-send-line text-white text-3xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                System Update Protocol
              </h2>
              <p className="text-slate-600 mb-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                Abonnieren Sie unseren Newsletter für die neuesten Strategien und Updates im Reputationsmanagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="E-Mail Gateway"
                  className="flex-1 px-6 py-3 bg-slate-50 border border-slate-200 rounded-lg text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all font-mono text-sm shadow-inner"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                />
                <button className="px-8 py-3 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-lg hover:bg-blue-900 hover:text-white transition-all duration-300 font-bold uppercase tracking-wider shadow-md hover:shadow-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Initialize
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
